import {Button, DataStatus, useLocalStorage} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useCompleteMicrosoftOAuth2Mutation} from '@dms/api/core';
import {getApiErrorMessages} from '@dms/api/utils';
import {appWorkspaceKey} from '@dms/config';
import {CONFIG_STORAGE_KEY, OAuthConfig} from '@dms/features/email-smtp';
import i18n from '@dms/i18n';
import {buildUrl, dashboardRoutes, microsoftOAuthRedirect, myProfileRoutes} from '@dms/routes';
import {CommonSkeleton, handleApiError} from '@dms/shared';

import {ApiError, useNavigate, useOnMount, useQueryState} from 'shared/web';

import {MicrosoftErrorCode} from './components/MicrosoftErrorCode';
import {useFinishEmailOAuth} from './hooks/useFInishEmailOAuth';

export function MicrosoftOAuthFinish() {
  const [code] = useQueryState('code');
  const [state] = useQueryState('state');
  const [error] = useQueryState('error');

  const [config] = useLocalStorage<OAuthConfig>(CONFIG_STORAGE_KEY, {purpose: 'email'});

  const navigate = useNavigate();

  const [
    completeMicrosoftOAuth2,
    {isError: isErrorCompleteMicrosoftOAuth2, error: errorCompleteMicrosoftOAuth2},
  ] = useCompleteMicrosoftOAuth2Mutation();

  const [finishEmailOAuth, {isError: isErrorFinishEmail, error: errorFinishEmail}] =
    useFinishEmailOAuth(config);

  const isApiError = isErrorCompleteMicrosoftOAuth2 || isErrorFinishEmail;
  const apiError: ApiError | undefined = errorCompleteMicrosoftOAuth2 ?? errorFinishEmail;

  useOnMount(() => {
    if (isNotNil(error) || isNil(code) || isNil(state)) {
      return;
    }

    const callbackUrl = buildUrl(microsoftOAuthRedirect, appWorkspaceKey, false);

    completeMicrosoftOAuth2({
      completeMicrosoftOAuth2RequestBody: {
        code,
        state,
        callbackUrl,
      },
    })
      .unwrap()
      .then(async (response) => {
        if (config.purpose === 'email') {
          await finishEmailOAuth(response);
        }

        if (config.purpose === 'calendar') {
          // setTimeout is needed because sometimes, BE returns none synced calendars on this page if we redirect
          setTimeout(() => navigate(myProfileRoutes.calendarSynchronization), 1000);
          localStorage.removeItem(CONFIG_STORAGE_KEY);
        }
      })
      .catch(handleApiError);
  });

  const getApiErrorMessage = () => {
    if (!apiError) {
      return undefined;
    }

    const errors = getApiErrorMessages(apiError);
    return isArray(errors) ? errors[0] : errors;
  };

  return (
    <Center width="100%" height="100%">
      <VStack spacing={4}>
        <DataStatus
          isError={isNotNil(error)}
          errorMessage={`${i18n.t('page.settings.labels.microsoftOAuthFinishError')} (${i18n.t('page.settings.labels.microsoftOAuthFinishErrorDescription')})`}
        >
          <DataStatus isError={isApiError} errorMessage={getApiErrorMessage()}>
            <CommonSkeleton />
          </DataStatus>
        </DataStatus>

        <Show when={isNotNil(error)}>
          <MicrosoftErrorCode />
        </Show>

        <Show when={isNotNil(error) || isApiError}>
          <Center>
            <Button
              leftIcon="action/arrow_right_alt"
              title={i18n.t('page.homepage.labels.title')}
              onClick={() => navigate(dashboardRoutes.home)}
            />
          </Center>
        </Show>
      </VStack>
    </Center>
  );
}
